/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The clients list on a phone: one row per person, and one thing to do.
 *
 * The table's six columns do not survive a 360px screen — they either wrap
 * into an unreadable block or hide the columns that matter. A row instead
 * carries the facts that decide whether you tap it — who they are and what
 * they are after — and puts the action that is actually taken from a list
 * — opening WhatsApp — where a thumb can reach it.
 *
 * It does not carry a badge for the kind of client. That was a derived
 * word, not a stored one, and the form no longer asks the question it was
 * derived from; what the form does ask is the demand, which says more in
 * the same space and differs from row to row.
 *
 * That button is deliberately outside the row's own tap target: reaching for
 * WhatsApp and landing on the client record is the single most annoying
 * misfire this screen can produce, so the two targets are separated and the
 * button stops the row's click from propagating.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Anchor, PersonIcon, Text } from '@/shared/ui';
import { WhatsAppIcon } from '@/shared/ui/components/WhatsAppQRCode.parts';
import { buildWhatsAppLink } from '@/shared/utils/whatsapp-link';
import { formatRelativeDay } from '@/shared/utils/date';
import { hasUnseenSocialUpdate } from '@/shared/utils/social-lead-activity';
import { EmptyState } from '@/shared/ui/components/basic/EmptyState';
import type { ClientRow } from '../../context/ClientManagementContext';
import ClientAvatar from '../ClientAvatar';
import { SocialUnseenDot } from '../SocialUnseenDot';
import { getClientAvatarAccentColor } from '../../utils/avatar-accent';
import { describeClient } from '../../utils/client-summary';

/** WhatsApp's own brand colours; the button is a quote of their badge. */
const WHATSAPP_GREEN = '#25D366';
const WHATSAPP_DARK = '#128C7E';

interface ClientsMobileCardViewProps {
  clients: ClientRow[];
  hasActiveQuery: boolean;
  onRowNavigate: (clientId: string) => void;
  onAddClient: () => void;
  t: (key: string) => string;
}

/**
 * Resolves the client's WhatsApp number.
 *
 * Only a contact explicitly typed `whatsapp` counts — a `phone` entry is not
 * a promise that the number is on WhatsApp, and sending somebody to a dead
 * chat is worse than not offering the button.
 */
function resolveWhatsAppNumber(client: ClientRow): string {
  return client.contacts?.find((contact) => contact.type === 'whatsapp')?.value ?? '';
}

/** Phrases the last contact, or returns `''` when there has not been one. */
function lastContactLabelFor(
  client: ClientRow,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const when = formatRelativeDay(client.last_contact_date);
  return when ? t('list.summary.lastContact', { when }) : '';
}

/**
 * ClientsMobileCardView component.
 */
export function ClientsMobileCardView({
  clients,
  hasActiveQuery,
  onRowNavigate,
  onAddClient,
  t,
}: ClientsMobileCardViewProps) {
  const { t: tLocal } = useTranslation('features/clients');

  if (clients.length === 0) {
    return (
      <EmptyState
        icon={<PersonIcon width={28} height={28} style={{ color: 'var(--text-tertiary)', opacity: 0.5 }} />}
        title={hasActiveQuery ? t('list.noResults.title') : t('list.empty.title')}
        description={hasActiveQuery ? t('list.noResults.description') : t('list.empty.description')}
        action={hasActiveQuery ? undefined : { label: t('list.empty.addClient'), onClick: onAddClient }}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      {clients.map((client) => (
        <ClientCard
          key={client.client_id}
          client={client}
          summary={describeClient(client, tLocal)}
          lastContactLabel={lastContactLabelFor(client, tLocal)}
          whatsAppLabel={tLocal('list.openWhatsApp')}
          onNavigate={() => onRowNavigate(client.client_id)}
        />
      ))}
    </div>
  );
}

interface ClientCardProps {
  client: ClientRow;
  /** What this client is after, already built and translated. */
  summary: string;
  /** When you last spoke, already phrased; empty when you never have. */
  lastContactLabel: string;
  whatsAppLabel: string;
  onNavigate: () => void;
}

const ClientCard: React.FC<ClientCardProps> = ({
  client, summary, lastContactLabel, whatsAppLabel, onNavigate,
}) => {
  const number = useMemo(() => resolveWhatsAppNumber(client), [client]);

  return (
    <div
      data-testid="client-card"
      onClick={onNavigate}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        minHeight: 64,
        padding: 'var(--space-2-5) var(--space-3)',
        background: 'var(--surface-primary)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-sm)',
        cursor: 'pointer',
        fontFamily: 'var(--font-family)',
      }}
    >
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <ClientAvatar
          fullName={client.full_name}
          size={40}
          accentColor={getClientAvatarAccentColor(client.full_name)}
        />
        <SocialUnseenDot show={hasUnseenSocialUpdate(client.client_id, client.lead_interactions)} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
        <Text
          component="span"
          className="truncate"
          style={{
            fontSize: 'var(--text-body-sm)',
            fontWeight: 'var(--font-semibold)' as unknown as number,
            color: 'var(--text-primary)',
          }}
        >
          {client.full_name}
        </Text>

        {summary && (
          <Text
            component="span"
            className="truncate"
            style={{ fontSize: 'var(--text-meta)', color: 'var(--text-secondary)' }}
          >
            {summary}
          </Text>
        )}

        {/* Only when somebody has actually been spoken to. Rendering "—" for
            the majority that have not filled the list with a column of
            dashes that answered nothing. */}
        {lastContactLabel && (
          <Text
            component="span"
            className="truncate"
            style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)' }}
          >
            {lastContactLabel}
          </Text>
        )}
      </div>

      {number && (
        <Anchor
          href={buildWhatsAppLink(number)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="client-card-whatsapp"
          aria-label={`${whatsAppLabel}: ${client.full_name}`}
          onClick={(e) => e.stopPropagation()}
          className="flex shrink-0 items-center justify-center no-underline!"
          style={{
            width: 40,
            height: 40,
            borderRadius: 'var(--radius-md)',
            border: `1px solid ${WHATSAPP_GREEN}`,
            background: `color-mix(in srgb, ${WHATSAPP_GREEN} 10%, transparent)`,
            color: WHATSAPP_DARK,
          }}
        >
          <WhatsAppIcon className="h-5 w-5" />
        </Anchor>
      )}
    </div>
  );
};

export default ClientsMobileCardView;
